import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import type { PurposeInput } from './api';

type TextField = 'code' | 'label';
export function PurposeFields({
  value,
  onChange,
  creating,
  disabled,
}: {
  readonly value: PurposeInput;
  readonly onChange: (value: PurposeInput) => void;
  readonly creating: boolean;
  readonly disabled: boolean;
}) {
  return (
    <fieldset disabled={disabled} className="space-y-4">
      <PurposeTextField
        field="code"
        value={value.code}
        readOnly={!creating}
        onChange={(code) => onChange({ ...value, code })}
      />
      <PurposeTextField
        field="label"
        value={value.label}
        onChange={(label) => onChange({ ...value, label })}
      />
      <PurposeTypeFields value={value} onChange={onChange} />
    </fieldset>
  );
}
function PurposeTextField({
  field,
  value,
  readOnly = false,
  onChange,
}: {
  readonly field: TextField;
  readonly value: string;
  readonly readOnly?: boolean;
  readonly onChange: (value: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const id = useId();
  const [touched, setTouched] = useState(false);
  const valid = field === 'code' ? /^[A-Za-z0-9_-]{1,20}$/.test(value) : !!value.trim();
  const invalid = touched && !readOnly && !valid;
  return (
    <div className="space-y-1 text-sm">
      <label htmlFor={id} className="font-semibold">
        {t(`configuration.${field}`)}
      </label>
      <Input
        id={id}
        value={value}
        readOnly={readOnly}
        maxLength={field === 'code' ? 20 : 200}
        aria-invalid={invalid}
        aria-describedby={invalid ? `${id}-error` : undefined}
        onBlur={() => setTouched(true)}
        onChange={(event) => {
          setTouched(true);
          onChange(event.target.value);
        }}
      />
      {invalid ? (
        <small id={`${id}-error`} className="text-destructive">
          {t(`configuration.${field}Hint`)}
        </small>
      ) : null}
    </div>
  );
}
function PurposeTypeFields({
  value,
  onChange,
}: {
  readonly value: PurposeInput;
  readonly onChange: (value: PurposeInput) => void;
}) {
  const { t } = useTranslation('expenses');
  const id = useId();
  const [touched, setTouched] = useState(false);
  const invalid = touched && value.noteTypes.length === 0;
  return (
    <fieldset
      className="space-y-1"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setTouched(true);
      }}
      aria-describedby={invalid ? id : undefined}
    >
      <legend className="text-sm font-semibold">{t('configuration.noteTypes')}</legend>
      {(['travel', 'reimbursement'] as const).map((type) => (
        <label key={type} className="flex min-h-11 items-center gap-3 text-sm">
          <Checkbox
            checked={value.noteTypes.includes(type)}
            aria-invalid={invalid}
            aria-describedby={invalid ? id : undefined}
            onChange={() => {
              setTouched(true);
              onChange({
                ...value,
                noteTypes: value.noteTypes.includes(type)
                  ? value.noteTypes.filter((v) => v !== type)
                  : [...value.noteTypes, type].sort(),
              });
            }}
          />
          {t(`reports.noteType.${type}`)}
        </label>
      ))}
      {invalid ? (
        <p id={id} className="text-destructive text-sm">
          {t('configuration.typesHint')}
        </p>
      ) : null}
    </fieldset>
  );
}
